////////////////////////////////////////
// # GOLDEN RULE!!!! HONOR ABOVE ALL!!!!
////////////////////////////////////////
// NO NEW FILES!!!!!!!!!
////////////////////////////////////////

// BoredAndAmbitious_Home_SRC_Homepage_Lobby_Pea.tsx
// State management and business logic for B&A Homepage

/*
═══════════════════════════════════════════════════════════════════════════════════════
  IMPORTS
═══════════════════════════════════════════════════════════════════════════════════════
*/

import { useState, useCallback } from 'react';
import type { Episode, NavigationItem, SocialLink, HeroContent, FeatureCard } from '../../../types/BoredAndAmbitious_Home_SRC_Types.js';

/*
═══════════════════════════════════════════════════════════════════════════════════════
  STATIC DATA - Navigation, Social Links, Hero Content
═══════════════════════════════════════════════════════════════════════════════════════
*/

export const NAVIGATION_ITEMS: NavigationItem[] = [
  { label: 'Episodes', href: '#episodes' },
];

// T_RSS_FEED_2026_01_17: Anchor/Spotify RSS feed for episode data
export const RSS_FEED_URL = 'https://anchor.fm/s/10ddc644c/podcast/rss';
export const SPOTIFY_URL = 'https://open.spotify.com/show/3ksIn68VJ0PY6RWwh3bAsZ';
export const APPLE_PODCASTS_URL = 'https://podcasts.apple.com/us/podcast/bored-and-ambitious/id1869416326';
export const AUDIBLE_URL = 'https://www.audible.com/podcast/Bored-and-Ambitious/B0GH2G8JNX';
export const AMAZON_MUSIC_URL = 'https://music.amazon.com/podcasts/bf69aae2-6c4a-4537-ac43-de762e0e769f/bored-and-ambitious';

export const SOCIAL_LINKS: SocialLink[] = [
  { platform: 'spotify', url: SPOTIFY_URL, label: 'Spotify' },
  { platform: 'apple', url: APPLE_PODCASTS_URL, label: 'Apple Podcasts' },
  { platform: 'amazon', url: AMAZON_MUSIC_URL, label: 'Amazon Music' },
  { platform: 'audible', url: AUDIBLE_URL, label: 'Audible' },
];

export const HERO_CONTENT: HeroContent = {
  headline: 'Long-Form Narrative History',
  subheadline: 'Exhaustively researched episodes tracing the people, systems, and accidents that built the modern world. 25+ episodes, 2-3 hours each.',
  ctaText: 'Listen on Spotify',
  ctaLink: 'https://open.spotify.com/show/3ksIn68VJ0PY6RWwh3bAsZ',
};

export const FEATURE_CARDS: FeatureCard[] = [
  {
    icon: '📚',
    title: 'Exhaustively Researched',
    description: 'Every episode draws from primary sources, academic literature, and expert consultations. No shortcuts.',
  },
  {
    icon: '🎧',
    title: '2-3 Hours Deep',
    description: 'Long-form narrative that gives complex topics the time they deserve. Perfect for commutes, workouts, or deep focus.',
  },
  {
    icon: '🌍',
    title: 'The Stories That Built Today',
    description: 'From gunpowder to the transistor, from the Spice Trade to Standard Oil—the hidden forces that shaped the modern world.',
  },
];

// T_REAL_EPISODES_2026_01_17: Real episodes from Anchor RSS feed
export const PODCAST_IMAGE = 'https://d3t3ozftmdmh3i.cloudfront.net/staging/podcast_uploaded_nologo/45175147/45175147-1768484384539-4adbfaa457d5d.jpg';

export const FEATURED_EPISODES: Episode[] = [
  {
    id: 'gunpowder',
    title: 'Gunpowder',
    subtitle: 'The Accidental Invention That Ended the Medieval World',
    description: 'In a smoky monastery workshop during the Tang dynasty, an alchemist searching for immortality found the formula for death.',
    thumbnailUrl: PODCAST_IMAGE,
    duration: '2h 24m',
    category: 'medieval',
    publishedAt: '2026-01-17',
    featured: true,
    url: 'https://spotifycreators-web.app.link/e/WgJJGh1e0Zb',
  },
  {
    id: 'holy-roman-empire',
    title: 'Holy Roman Empire',
    subtitle: 'Neither Holy, Nor Roman, Nor an Empire',
    description: 'The thousand-year political entity that defined medieval Europe—and defied every attempt to understand it.',
    thumbnailUrl: PODCAST_IMAGE,
    duration: '3h 15m',
    category: 'medieval',
    publishedAt: '2026-01-17',
    featured: true,
    url: 'https://spotifycreators-web.app.link/e/BaGGS7Ql0Zb',
  },
  {
    id: 'congo-free-state',
    title: 'Congo Free State, 1885-1908',
    subtitle: 'The Horror at the Heart of Darkness',
    description: 'How one man\'s private colony became the site of one of history\'s greatest atrocities.',
    thumbnailUrl: PODCAST_IMAGE,
    duration: '3h 8m',
    category: 'modern',
    publishedAt: '2026-01-16',
    featured: true,
    url: 'https://spotifycreators-web.app.link/e/rfPwC0HnZZb',
  },
  {
    id: 'industrial-revolution',
    title: 'Industrial Revolution',
    subtitle: 'The Great Transformation of Human Economic Existence',
    description: 'In two generations, humanity went from muscle power to machine power—and nothing was ever the same.',
    thumbnailUrl: PODCAST_IMAGE,
    duration: '2h 34m',
    category: 'economics',
    publishedAt: '2026-01-17',
    featured: true,
    url: 'https://spotifycreators-web.app.link/e/7X6WIyInZZb',
  },
  {
    id: 'smallpox',
    title: 'Smallpox',
    subtitle: 'The Only Disease Humanity Ever Killed',
    description: 'The centuries-long war against the virus that killed more people than all wars combined—and how we finally won.',
    thumbnailUrl: PODCAST_IMAGE,
    duration: '1h 47m',
    category: 'science',
    publishedAt: '2026-01-17',
    featured: true,
    url: 'https://spotifycreators-web.app.link/e/pcp75zInZZb',
  },
  {
    id: 'spice-trade',
    title: 'The Spice Trade, 1400-1800',
    subtitle: 'The Trade Routes That Built Empires',
    description: 'How the quest for pepper, cinnamon, and nutmeg launched the age of exploration and colonialism.',
    thumbnailUrl: PODCAST_IMAGE,
    duration: '2h 58m',
    category: 'economics',
    publishedAt: '2026-01-16',
    featured: true,
    url: 'https://spotifycreators-web.app.link/e/sQpiU1HnZZb',
  },
];

/*
═══════════════════════════════════════════════════════════════════════════════════════
  HOOKS - Custom hooks for Homepage state
═══════════════════════════════════════════════════════════════════════════════════════
*/

export interface UseHomepageStateReturn {
  // State
  isMenuOpen: boolean;
  selectedCategory: string | null;
  isLoading: boolean;

  // Actions
  toggleMenu: () => void;
  closeMenu: () => void;
  selectCategory: (category: string | null) => void;

  // Data
  navigation: NavigationItem[];
  socialLinks: SocialLink[];
  heroContent: HeroContent;
  features: FeatureCard[];
  featuredEpisodes: Episode[];
}

export function useHomepageState(): UseHomepageStateReturn {
  // State
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [isLoading] = useState(false);

  // Actions
  const toggleMenu = useCallback(() => {
    setIsMenuOpen(prev => !prev);
  }, []);

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  const selectCategory = useCallback((category: string | null) => {
    setSelectedCategory(category);
  }, []);

  return {
    // State
    isMenuOpen,
    selectedCategory,
    isLoading,

    // Actions
    toggleMenu,
    closeMenu,
    selectCategory,

    // Data
    navigation: NAVIGATION_ITEMS,
    socialLinks: SOCIAL_LINKS,
    heroContent: HERO_CONTENT,
    features: FEATURE_CARDS,
    featuredEpisodes: FEATURED_EPISODES,
  };
}

/*
═══════════════════════════════════════════════════════════════════════════════════════
  EXPORTS
═══════════════════════════════════════════════════════════════════════════════════════
*/

export type { Episode, NavigationItem, SocialLink, HeroContent, FeatureCard };